import React, { useEffect, useState, useMemo } from "react";
import { axiosSalary, axiosMaster } from "../../api/axiosInstance";
import { FaFileExcel, FaSearch } from "react-icons/fa";

const DailyAttendanceReport = () => {

  /* ================= USER ================= */
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch { }
  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */
  const [date, setDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");

  /* ================= FETCH ================= */
  const fetchReport = async () => {
    if (!date) return;

    try {
      setLoading(true);

      const [attendanceRes, empRes] = await Promise.all([
        axiosSalary.get(
          `/DailyAttendanceReport/DailyReportByGroupAndDate?groupId=${groupId}&attendanceDate=${date}`
        ),
        axiosMaster.get(
          `/EmployeeMaster/GetByGroup?groupId=${groupId}`
        )
      ]);

      const attendance = attendanceRes.data || [];
      const employees = empRes.data || [];

      const merged = mergeAttendanceWithEmployees(attendance, employees);
      setData(merged);

    } catch (err) {
      console.error("Report error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= MERGE (IMPORTANT) ================= */
  const mergeAttendanceWithEmployees = (attendance, employees) => {

    const map = new Map();
    attendance.forEach(a => {
      map.set(a.empId, a);
    });

    return employees.map(emp => {

      if (map.has(emp.empId)) {
        const att = map.get(emp.empId);

        return {
          ...att,
          empName: att.empName || emp.empName
        };
      }

      // ❌ ABSENT ENTRY
      return {
        empId: emp.empId,
        empName: emp.empName,
        attendanceDateDisplay: date,

        firstPunchDisplay: null,
        lastPunchDisplay: null,
        totalPunches: 0,
        workingMinutes: 0,
        lateMinutes: 0,
        earlyLeaveMinutes: 0,
        overtimeMinutes: 0,

        isPresent: false,
        isAbsent: true,
        isLate: false,

        remarks: "Absent"
      };
    });
  };

  /* ================= DEFAULT DATE ================= */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);
  }, []);

  useEffect(() => {
    if (date) fetchReport();
  }, [date]);

  /* ================= SEARCH ================= */
  const filteredData = useMemo(() => {
    const term = search.toLowerCase();

    let filtered = data.filter(d =>
      d.empName?.toLowerCase().includes(term) ||
      String(d.empId).includes(term)
    );

    // ✅ APPLY SUMMARY FILTER
    if (activeFilter === "PRESENT") {
      filtered = filtered.filter(d => d.isPresent && !d.isAbsent);
    }
    else if (activeFilter === "ABSENT") {
      filtered = filtered.filter(d => d.isAbsent);
    }
    else if (activeFilter === "LATE") {
      filtered = filtered.filter(d => d.isLate && !d.isAbsent);
    }

    return filtered;

  }, [data, search, activeFilter]);

  useEffect(() => {
    setActiveFilter("ALL");
  }, [search]);

  /* ================= SUMMARY ================= */
  const total = data.length;

  const present = data.filter(d => d.isPresent && !d.isAbsent).length;

  const absent = data.filter(d => d.isAbsent).length;

  const late = data.filter(d => d.isLate && !d.isAbsent).length;

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      EmpId: r.empId,
      Name: `"${r.empName || ""}"`,
      Date: r.attendanceDateDisplay,
      FirstPunch: r.firstPunchDisplay || "",
      LastPunch: r.lastPunchDisplay || "",
      Punches: r.totalPunches,
      WorkingMinutes: r.workingMinutes,
      LateMinutes: r.lateMinutes,
      EarlyLeaveMinutes: r.earlyLeaveMinutes,
      OvertimeMinutes: r.overtimeMinutes,
      Status: r.isAbsent
        ? "Absent"
        : r.isLate
          ? "Late"
          : r.isPresent
            ? "Present"
            : "",
      Remarks: `"${r.remarks || ""}"`,
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `DailyAttendance_${date}.csv`;
    link.click();
  };

  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">

      {/* ================= HEADER ================= */}
     <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">

  {/* LEFT TITLE */}
  <div className="flex items-center justify-between w-full md:w-auto">
    <h2 className="text-lg md:text-xl font-semibold">
      Daily Attendance Report
    </h2>
  </div>

  {/* RIGHT CONTROLS */}
  <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">

    {/* SEARCH */}
    <div className="relative w-full sm:w-56">
      <FaSearch className="absolute left-2 top-2.5 text-gray-400 text-xs" />
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full pl-7 pr-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder="Search employee..."
      />
    </div>

    {/* DATE + BUTTON */}
    <div className="flex gap-2 w-full sm:w-auto">

      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="border px-2 py-1.5 rounded-md text-sm w-full sm:w-auto focus:ring-2 focus:ring-blue-400"
      />

      <button
        onClick={exportExcel}
        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center justify-center gap-2 w-full sm:w-auto transition"
      >
        <FaFileExcel /> Export
      </button>

    </div>

  </div>

</div>

      {/* ================= SUMMARY ================= */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">

        <div
          onClick={() => setActiveFilter("ALL")}
          className={`bg-gray-100 p-3 rounded text-center cursor-pointer ${activeFilter === "ALL" ? "ring-2 ring-gray-400" : ""}`}
        >
          <p>Total</p>
          <p className="font-semibold">{total}</p>
        </div>

        <div
          onClick={() => setActiveFilter("PRESENT")}
          className={`bg-green-100 p-3 rounded text-center cursor-pointer ${activeFilter === "PRESENT" ? "ring-2 ring-green-500" : ""}`}
        >
          <p className="text-green-700">Present</p>
          <p className="font-semibold">{present}</p>
        </div>

        <div
          onClick={() => setActiveFilter("ABSENT")}
          className={`bg-red-100 p-3 rounded text-center cursor-pointer ${activeFilter === "ABSENT" ? "ring-2 ring-red-500" : ""}`}
        >
          <p className="text-red-700">Absent</p>
          <p className="font-semibold">{absent}</p>
        </div>

        <div
          onClick={() => setActiveFilter("LATE")}
          className={`bg-yellow-100 p-3 rounded text-center cursor-pointer ${activeFilter === "LATE" ? "ring-2 ring-yellow-500" : ""}`}
        >
          <p className="text-yellow-700">Late</p>
          <p className="font-semibold">{late}</p>
        </div>

      </div>

      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">

        <table className="w-full text-xs border-collapse">

          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2">ID</th>
              <th className="border p-2 text-left">Name</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">First</th>
              <th className="border p-2">Last</th>
              <th className="border p-2">Punch</th>
              <th className="border p-2">Work</th>
              <th className="border p-2">Late</th>
              <th className="border p-2">Early</th>
              <th className="border p-2">OT</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Remarks</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="12" className="text-center p-4">Loading...</td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="12" className="text-center p-4">No data</td>
              </tr>
            ) : filteredData.map((row) => {

              const rowColor = row.isAbsent
                ? "bg-red-50"
                : row.isLate
                  ? "bg-yellow-50"
                  : "";

              return (
                <tr key={row.empId} className={`${rowColor} hover:bg-blue-50`}>

                  <td className="border p-2 text-center">{row.empId}</td>
                  <td className="border p-2">{row.empName}</td>
                  <td className="border p-2 text-center">{row.attendanceDateDisplay}</td>
                  <td className="border p-2">{row.firstPunchDisplay || "-"}</td>
                  <td className="border p-2">{row.lastPunchDisplay || "-"}</td>
                  <td className="border p-2 text-center">{row.totalPunches}</td>
                  <td className="border p-2 text-center">{row.workingMinutes}</td>
                  <td className="border p-2 text-center">{row.lateMinutes}</td>
                  <td className="border p-2 text-center">{row.earlyLeaveMinutes}</td>
                  <td className="border p-2 text-center">{row.overtimeMinutes}</td>

                  <td className="border p-2 text-center">
                    {row.isAbsent ? (
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">Absent</span>
                    ) : row.isLate ? (
                      <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">Late</span>
                    ) : row.isPresent ? (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Present</span>
                    ) : (
                      <span className="bg-gray-100 px-2 py-1 rounded text-xs">NA</span>
                    )}
                  </td>

                  <td className="border p-2 text-xs">{row.remarks}</td>

                </tr>
              );
            })}
          </tbody>

        </table>
      </div>

      {/* ================= MOBILE VIEW ================= */}
      <div className="md:hidden space-y-3 mt-3">

        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : filteredData.length === 0 ? (
          <div className="text-center text-gray-500">No data</div>
        ) : filteredData.map((row) => (

          <div key={row.empId} className="border rounded-xl p-3 shadow-sm">

            <div className="flex justify-between">
              <h3 className="font-semibold">{row.empName}</h3>
              {row.isAbsent ? "🔴" : row.isLate ? "🟡" : "🟢"}
            </div>

            <p className="text-xs text-gray-500">ID: {row.empId}</p>

            <div className="grid grid-cols-3 gap-2 text-xs mt-2 text-center">
              <div className="bg-gray-100 p-1 rounded">Punch<br />{row.totalPunches}</div>
              <div className="bg-blue-100 p-1 rounded">Work<br />{row.workingMinutes}</div>
              <div className="bg-yellow-100 p-1 rounded">Late<br />{row.lateMinutes}</div>
            </div>

            <div className="text-xs mt-2 space-y-1">
              <p><b>Date:</b> {row.attendanceDateDisplay}</p>
              <p><b>First:</b> {row.firstPunchDisplay || "-"}</p>
              <p><b>Last:</b> {row.lastPunchDisplay || "-"}</p>
              <p><b>Early Leave:</b> {row.earlyLeaveMinutes}</p>
              <p><b>OT:</b> {row.overtimeMinutes}</p>
              {row.remarks && <p><b>Remarks:</b> {row.remarks}</p>}
            </div>

          </div>
        ))}

      </div>

    </div>
  );
};

export default DailyAttendanceReport;